import { useEffect, useMemo, useState } from 'react';

import useFabricCanvas from 'editor/src/util/useFabricCanvas';

import {
  getActiveSelection,
  getActiveSelectionElementOffset,
  getActiveSelectionElementRect,
  getActiveSelectionRect,
} from 'editor/src/component/EditorArea/utils/groupSelectionUtils';

import { ObjectRect } from './Image/types';

// support multi selection positioning for elements
function useMultiSelection(uuid: number, frameRect: ObjectRect) {
  const fabricCanvas = useFabricCanvas();
  const [activeSelectionRect, setActiveSelectionRect] = useState<ObjectRect | undefined>(undefined);

  const activeSelectionOffset = useMemo(() => {
    return getActiveSelectionElementOffset(activeSelectionRect);
  }, [activeSelectionRect]);

  const selectableFrameRect = useMemo(() => {
    return getActiveSelectionElementRect(frameRect, activeSelectionOffset);
  }, [frameRect, activeSelectionOffset]);

  useEffect(() => {
    const onSelectionUpdated = () => {
      const newActiveSelection = getActiveSelection(fabricCanvas);
      const hasCurrentElementInSelection = newActiveSelection?.getObjects().find((fabricObject) => {
        return fabricObject.selectable && (fabricObject as any).uuid === uuid;
      });

      if (newActiveSelection && hasCurrentElementInSelection) {
        const newRect = getActiveSelectionRect(newActiveSelection);
        if (
          activeSelectionRect?.width !== newRect.width ||
          activeSelectionRect?.height !== newRect?.height ||
          activeSelectionRect?.left !== newRect?.left ||
          activeSelectionRect?.top !== newRect?.top ||
          activeSelectionRect?.angle !== newRect?.angle
        ) {
          setActiveSelectionRect(newRect);
        }
      } else {
        setActiveSelectionRect(undefined);
      }
    };

    fabricCanvas.on('selection:created', onSelectionUpdated);
    fabricCanvas.on('selection:updated', onSelectionUpdated);
    fabricCanvas.on('selection:cleared', onSelectionUpdated);
  }, [uuid]);

  return { activeSelectionOffset, selectableFrameRect, hasActiveSelection: !!activeSelectionRect?.left };
}

export default useMultiSelection;
